<template>
  <AuthCard>
    <template #form>
      <form class="form-box login-box">
        <div class="alert-box">
          <v-alert dismissible color="error" v-show="!!validation_message">
            {{ validation_message }}
          </v-alert>
        </div>
        <div class="card-header">
          <div class="logo"></div>
        </div>
        <div class="card-body">
          <div class="input-container" dir="ltr">
            <Input
              v-model="form.sub_domain"
              :label="$t('sub_domain')"
              :placeholder="$t('sub_domain')"
              class="input-md rtl-text-align-start"
              :error-messages="error.sub_domain && $t(`${error.sub_domain}`)"
              autocomplete="off"
              outlined
              @keyup="() => validateForm('sub_domain')"
            />
            <span class="domain-title"> .posrocket.com </span>
          </div>
          <div class="input-container">
            <Input
              :label="$t('email_address')"
              :placeholder="$t('email_address')"
              :error-messages="error.email && $t(`${error.email}`)"
              autocomplete="off"
              v-model="form.email"
              outlined
              @keyup="() => validateForm('email')"
            />
          </div>
          <div class="input-container">
            <Input
              :label="$t('password')"
              :placeholder="$t('password')"
              :type="is_password_visible ? 'text' : 'password'"
              v-model="form.password"
              :outlined="true"
              :error-messages="error.password && $t(`${error.password}`)"
              autocomplete="off"
              @keyup="() => validateForm('password')"
              :append-icon="is_password_visible ? 'visibility' : 'visibility_off'"
              @click:append="() => (is_password_visible = !is_password_visible)"
            />
          </div>
        </div>

        <div class="remmber-box">
          <v-checkbox v-model="form.is_remamber" color="primary" :label="$t('remember_me')" class="check-box" />
          <Link :title="$t('reset_password')" class="link" :to="'reset-password'" />
        </div>
        <div class="card-footer">
          <Button
            color="primary"
            class="mb-5"
            elevation="0"
            block
            large
            :loading="is_loading"
            :onClick="login"
            :disabled="!is_submit_enabled || is_loading"
            :title="$t('login')"
          />

          <Button
            color="default"
            elevation="0"
            class="mt-2"
            block
            large
            text
            :onClick="HandleSwitchAppLang"
            :title="$t('footer.switch_lang')"
          />
        </div>
      </form>
    </template>
  </AuthCard>
</template>

<script>
import { Helper, Lang } from "@/services/helper";
import AuthCard from "@/components/modules/auth/index.vue";
import { UserInterface } from "@/interface/user.interface";
import { mapActions, mapGetters, mapMutations } from "vuex";
import Storage from "@/services/storage";
import * as yup from "yup";
import Vue from "vue";
import "./styles.scss";
import { StorageConstants } from "@/enums/storage.enum";
import { $eventHub } from "@/components";
import { EventConstraints } from "@/enums/events.enum";
import { setupUserAbilities } from "@/services/ability";
import { LinksConstrains } from "@/enums/links.enum";

const loginFormSchema = yup.object().shape({
  email: yup.string().required("invalid_email").email("invalid_email"),
  password: yup.string().required("invalid_password"),
  sub_domain: yup.string().required("invalid_sub_domain"),
});

const LoginPage = Vue.extend({
  name: "LoginPage",

  components: { AuthCard },

  mounted() {
    this.$nextTick(() => {
      const saved_login_credentials = Storage.get(StorageConstants.CREDENTIALS);
      if (!!saved_login_credentials) {
        this.form = {
          ...this.form,
          ...saved_login_credentials,
        };
      }
      this.validateAll();
      $eventHub.$emit(EventConstraints.HIDE_APP_LOADER);
    });

    window.addEventListener("keyup", this.handleOnEnterClick);
  },

  destroyed() {
    window.removeEventListener("keyup", this.handleOnEnterClick);
  },

  data() {
    return {
      form: {
        sub_domain: "",
        email: "",
        password: "",
        is_remamber: false,
      },
      is_loading: false,
      is_submit_enabled: false,
      is_password_visible: false,
      validation_message: "",
      error: {
        email: "",
        password: "",
        sub_domain: "",
      },
    };
  },

  watch: {
    userMessage: {
      immediate: true,
      deep: true,
      handler(message) {
        if (!!message) {
          this.validation_message = message;
        }
      },
    },
  },

  computed: {
    ...mapGetters({
      isLoginSuccess: "User/success",
      userSession: "User/session",
      userMessage: "User/message",
      responseError: "User/error",
    }),
  },
  methods: {
    ...mapActions({
      doLogin: "User/doLogin",
    }),

    ...mapMutations({
      setUserLoggedIn: "User/setUserLoggedIn",
    }),

    /**
     * Validate login form data
     * @return {*}
     */
    validateForm(field) {
      return loginFormSchema
        .validateAt(field, this.form)
        .then(() => {
          this.error[field] = "";
          this.is_submit_enabled = true;
        })
        .catch((err) => {
          this.is_submit_enabled = false;
          this.error[field] = err.message;
        });
    },

    async login() {
      /**
       * On login request success
       * @return {void}
       */
      const onSuccess = async () => {
        const { email, password, sub_domain, is_remamber } = this.form;
        const userData = {
          email,
          password,
          sub_domain,
        };

        // Reset the saved CREDENTIALS
        Storage.remove(StorageConstants.CREDENTIALS);
        if (is_remamber) {
          // Set CREDENTIALS
          Storage.set(StorageConstants.CREDENTIALS, { ...userData, is_remamber });
        }
        await this.doLogin(userData);
        const isLoginSuccess = !!this.userSession;
        if (isLoginSuccess) {
          // setup the user abilities
          setupUserAbilities();
          // redirect user to main page after login
          return this.redirectUser(this.userSession);
        }

        const { message, details } = this.responseError;

        this.validation_message = message || this.userMessage || this.$t("fill_all_required_fields_message");

        if (details)
          this.error = {
            ...this.error,
            ...details,
          };

        this.is_loading = false;
      };

      /**
       * Handle login request error
       * @return {void}
       */
      const onError = (err) => {
        console.log(err);
        this.is_loading = false;
        this.validation_message = this.$t("fill_all_required_fields_message");
      };

      this.is_loading = true;
      return loginFormSchema.validate(this.form).then(onSuccess).catch(onError);
    },

    /***
     * Switch user data
     * @return {void}
     */
    HandleSwitchAppLang() {
      Lang.switch();
    },

    /***
     * Validate login form data
     * @return {void}
     */
    validateAll() {
      loginFormSchema
        .validate(this.form)
        .then(() => {
          this.is_submit_enabled = true;
        })
        .catch(() => {
          this.is_submit_enabled = false;
        });
    },

    /**
     * Redirect user depend on the role
     * @param {Object} session
     * @return {void | *}
     */
    redirectUser(session) {
      // get saved next URI
      const nextPath = Storage.get(StorageConstants.NEXT_ROUTE);
      if (!!nextPath) {
        // move to next URI after logged in
        Storage.remove(StorageConstants.NEXT_ROUTE);
        return this.$router.replace(nextPath).catch(() => {});
      }

      if (process.env.NODE_ENV === "production") {
        return this.$router.replace(LinksConstrains.CALL_CENTER_DASHBOARD).catch(() => {});
      }

      if (session.is_agent) {
        return this.$router.replace(LinksConstrains.CALL_CENTER_DASHBOARD).catch(() => {});
      }
      this.$router.push("/");
    },

    /**
     * Handle on user click enter on page
     * @param {Object} e
     * @return {void | *}
     */
    handleOnEnterClick(e) {
      if (e?.code !== "Enter") {
        return;
      }
      this.login();
    },
  },
});
export default LoginPage;
</script>
